// AttachmentManager.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../api/api';
import { AttachmentForm } from '../components/forms';
import './AttachmentManager.css';

// Add file type icons mapping
const fileTypeIcons = {
  // Microsoft Office
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '/icons/excel.png',
  'application/vnd.ms-excel': '/icons/excel.png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '/icons/word.png',
  'application/msword': '/icons/word.png',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '/icons/powerpoint.png',
  'application/vnd.ms-powerpoint': '/icons/powerpoint.png',
  // PDF
  'application/pdf': '/icons/pdf.png',
  // Default
  'default': '/icons/file.png'
};

// Helper function to get the appropriate icon for a file type
const getFileIcon = (fileType) => {
  return fileTypeIcons[fileType] || fileTypeIcons.default;
};

// Helper function to format date as DD-MMM-YYYY
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Helper function to get server URL
const getServerUrl = () => {
  // Get the protocol (http or https)
  const protocol = window.location.protocol;
  // Get the hostname (could be IP or domain)
  const hostname = window.location.hostname;
  // Get the port from localStorage baseUrl or default to 3001
  const baseUrl = localStorage.getItem('apiBaseUrl') || 'http://localhost:3001';
  const port = baseUrl.split(':')[2] || '3001';

  // Construct the full URL
  const serverUrl = `${protocol}//${hostname}:${port}`;
  console.log('Server URL constructed:', serverUrl);
  return serverUrl;
};

// Helper function to create download URL
const getFileUrl = (baseUrl, filename) => {
  // Get the correct server URL
  const serverUrl = getServerUrl();
  console.log('Original baseUrl:', baseUrl);
  console.log('Server URL:', serverUrl);
  console.log('Filename:', filename);
  
  // Create the full URL
  const fileUrl = `${serverUrl}/uploads/${encodeURIComponent(filename)}`;
  console.log('Final file URL:', fileUrl);
  return fileUrl;
};

// Helper function to handle file download
const handleFileDownload = async (baseUrl, filename, fileType) => {
  try {
    const fileUrl = getFileUrl(baseUrl, filename);
    console.log('Attempting to download from:', fileUrl);
    
    // For mobile devices, we'll use a fetch request to handle the download
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      console.log('Mobile device detected, using fetch');
      const response = await fetch(fileUrl);
      console.log('Fetch response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log('Blob received:', blob.type, blob.size);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // For debugging on mobile
      console.log('Download link created:', {
        href: link.href,
        download: link.download
      });
      
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } else {
      console.log('Desktop device detected, using window.open');
      window.open(fileUrl, '_blank');
    }
  } catch (error) {
    console.error('Download error details:', error);
    alert(`Failed to download file: ${error.message}`);
  }
};

const renderThumbnail = (filename, filetype, baseUrl) => {
  const fileUrl = getFileUrl(baseUrl, filename);
  console.log('Rendering thumbnail for:', fileUrl);
  
  if (filetype.startsWith('image/')) {
    return (
      <div className="thumbnail-container">
        <img 
          src={fileUrl} 
          alt="preview" 
          className="file-thumbnail"
          onClick={() => handleFileDownload(baseUrl, filename, filetype)}
          onError={(e) => {
            console.error('Image load error for:', fileUrl);
            e.target.onerror = null;
            e.target.src = getFileIcon('default');
          }}
        />
      </div>
    );
  } else {
    const iconPath = getFileIcon(filetype);
    return (
      <div className="thumbnail-container">
        <img 
          src={iconPath} 
          alt={filetype} 
          className="file-icon"
          onClick={() => handleFileDownload(baseUrl, filename, filetype)}
        />
      </div>
    );
  }
};

// TreeView component for grouped display
const TreeView = ({ wells, attachments, baseUrl, onDelete, onSelectForLink, renderThumbnail, getWellName }) => {
  const groupedAttachments = wells.reduce((acc, well) => {
    acc[well.WellID] = attachments.filter(att => att.WellID === well.WellID);
    return acc;
  }, {});

  // Add unlinked attachments group
  groupedAttachments['unlinked'] = attachments.filter(att => !att.WellID);

  return (
    <div className="tree-view">
      {/* Unlinked attachments section */}
      <div className="tree-group">
        <div className="tree-group-header">
          <h3>Unlinked Attachments</h3>
        </div>
        <div className="tree-group-content">
          {groupedAttachments['unlinked'].map(att => (
            <div key={att.AttachmentID} className="tree-item">
              {renderThumbnail(att.FileName, att.FileType, baseUrl)}
              <div className="tree-item-details">
                <button 
                  className="file-link-button"
                  onClick={() => handleFileDownload(baseUrl, att.FileName, att.FileType)}
                >
                  {att.FileName}
                </button>
                <p>{att.Description}</p>
                <div className="tree-item-actions">
                  <button onClick={() => onSelectForLink(att.AttachmentID)}>Link</button>
                  <button onClick={() => onDelete(att.AttachmentID)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Well groups */}
      {wells.map(well => (
        <div key={well.WellID} className="tree-group">
          <div className="tree-group-header">
            <h3>{well.WellName}</h3>
            <span className="attachment-count">
              {groupedAttachments[well.WellID].length} attachments
            </span>
          </div>
          <div className="tree-group-content">
            {groupedAttachments[well.WellID].map(att => (
              <div key={att.AttachmentID} className="tree-item">
                {renderThumbnail(att.FileName, att.FileType, baseUrl)}
                <div className="tree-item-details">
                  <button 
                    className="file-link-button"
                    onClick={() => handleFileDownload(baseUrl, att.FileName, att.FileType)}
                  >
                    {att.FileName}
                  </button>
                  <p>{att.Description}</p>
                  <div className="tree-item-actions">
                    <button onClick={() => onSelectForLink(att.AttachmentID)}>Link</button>
                    <button onClick={() => onDelete(att.AttachmentID)}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AttachmentManager() {
  const [searchParams] = useSearchParams();
  const [attachments, setAttachments] = useState([]);
  const [wells, setWells] = useState([]);
  const [dailyReports, setDailyReports] = useState([]);
  const [filterWellId, setFilterWellId] = useState('all');
  const [linkWellId, setLinkWellId] = useState('');
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [toast, setToast] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'tree'
  const [isUploadFormVisible, setIsUploadFormVisible] = useState(false);

  // Get base URL once when component mounts
  const baseUrl = useMemo(() => {
    const url = getServerUrl();
    console.log('Component baseUrl:', url);
    return url;
  }, []);
  
  const getWellName = useCallback((id) => {
    if (!id) return <span style={{ color: 'red', fontWeight: 'bold' }}>Unlinked</span>;
    const found = wells.find(w => w.WellID === id);
    return found ? found.WellName : id;
  }, [wells]);

  const loadInitialData = useCallback(async () => {
    const parseApiResponse = (response) => Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    let reportsData = [];

    try {
      try {
        const rawReportsRes = await apiFetch('/api/ddr'); // Attempt to fetch all reports
        reportsData = parseApiResponse(rawReportsRes);
      } catch (reportsErr) {
        if (reportsErr.message && reportsErr.message.toLowerCase().includes('missing wellid')) {
          console.warn('AttachmentManager: Could not load all daily reports for dropdown. The API endpoint (/api/ddr) seems to require a wellId. Report linking feature will be affected.');
          setToast('Warning: Could not load all daily reports. Linking to reports may be unavailable.');
        } else {
          throw reportsErr; // Re-throw other report errors
        }
      }

      const [attRes, wellsRes] = await Promise.all([
        apiFetch('/api/attachments'),
        apiFetch('/api/wells'),
      ]);

      setAttachments(parseApiResponse(attRes));
      setWells(parseApiResponse(wellsRes));
      setDailyReports(reportsData); // Set reports (empty if specific error occurred)

    } catch (err) {
      console.error('Load initial data error in AttachmentManager:', err);
      setToast(`Failed to load initial data: ${err.message || 'Unknown error'}`);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
    const wellIdFromUrl = searchParams.get('wellId');
    if (wellIdFromUrl) {
      setFilterWellId(wellIdFromUrl);
    }
  }, [loadInitialData, searchParams]);



  const handleLink = async () => {
    if (!selectedAttachmentId || !linkWellId) {
      setToast('Please select an attachment and a well to link.');
      return;
    }
    try {
      await apiFetch(`/api/attachments/${selectedAttachmentId}/link`, {
        method: 'PUT',
        body: JSON.stringify({ WellID: linkWellId }),
        headers: { 'Content-Type': 'application/json' }
      });
      setToast('Attachment linked successfully!');
      // Auto-dismiss success toast after 2 seconds
      setTimeout(() => setToast(''), 2000);
      loadInitialData();
      setSelectedAttachmentId(null);
      setLinkWellId('');
    } catch (err) {
      setToast('Link failed: ' + err.message);
    }
  };

  const handleDeleteAttachment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    try {
      await apiFetch(`/api/attachments/${id}`, { method: 'DELETE' });
      setToast('Attachment deleted successfully!');
      // Auto-dismiss success toast after 2 seconds
      setTimeout(() => setToast(''), 2000);
      loadInitialData();
    } catch (err) {
      setToast('Delete failed: ' + err.message);
    }
  };

  const onSelectForLink = (attachmentId) => {
    setSelectedAttachmentId(attachmentId);
    const currentAttachment = attachments.find(att => att.AttachmentID === attachmentId);
    if (currentAttachment && currentAttachment.WellID) {
        setLinkWellId(String(currentAttachment.WellID));
    }
  };

  return (
    <div className="attachment-manager-container">
      <h2>Attachment Manager</h2>
      {toast && <div className="toast">{toast}</div>}

      <div className="add-attachment-toggle">
        <button onClick={() => setIsUploadFormVisible(!isUploadFormVisible)}>
          {isUploadFormVisible ? 'Cancel Upload' : '+ Add New Attachment'}
        </button>
      </div>

      {isUploadFormVisible && (
        <AttachmentForm
          isVisible={isUploadFormVisible}
          wells={wells}
          dailyReports={dailyReports}
          onClose={() => setIsUploadFormVisible(false)}
          onSuccess={(message) => {
            setToast(message);
            setIsUploadFormVisible(false);
            loadInitialData();
          }}
          title="Upload New Attachment"
        />
      )}

      <hr />

      <div className="view-controls">
        <label>View Mode: </label>
        <select value={viewMode} onChange={(e) => setViewMode(e.target.value)}>
          <option value="table">Table</option>
          <option value="tree">Tree (Grouped by Well)</option>
        </select>
        {viewMode === 'table' && (
          <>
            <label style={{marginLeft: '20px'}}>Filter by Well: </label>
            <select value={filterWellId} onChange={(e) => setFilterWellId(e.target.value)}>
              <option value="all">All Wells</option>
              <option value="unlinked">Unlinked</option>
              {wells.map(w => <option key={w.WellID} value={w.WellID}>{w.WellName}</option>)}
            </select>
          </>
        )}
      </div>

      {viewMode === 'table' ? (
        <table className="attachments-table">
          <thead>
            <tr>
              <th>Icon</th>
              <th>File Name</th>
              <th>Subject</th>
              <th>Type</th>
              <th>Size (KB)</th>
              <th>Well</th>
              <th>Uploaded By</th>
              <th>Uploaded At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attachments
              .filter(att => filterWellId === 'all' || 
                             (filterWellId === 'unlinked' && !att.WellID) || 
                             (att.WellID && att.WellID.toString() === filterWellId))
              .map(att => (
                <tr key={att.AttachmentID}>
                  <td>{renderThumbnail(att.FileName, att.FileType, baseUrl)}</td>
                  <td>
                    <button 
                      className="file-link-button"
                      onClick={() => handleFileDownload(baseUrl, att.FileName, att.FileType)}
                      title={att.Description || att.FileName}
                    >
                      {att.FileName}
                    </button>
                  </td>
                  <td>{att.Subject || '-'}</td>
                  <td>{att.FileType || '-'}</td>
                  <td>{att.FileSize ? (att.FileSize / 1024).toFixed(2) : '-'}</td>
                  <td>{getWellName(att.WellID)}</td>
                  <td>{att.UploadedBy || '-'}</td>
                  <td>{att.UploadedAt ? new Date(att.UploadedAt).toLocaleDateString() : '-'}</td>
                  <td>
                    <button onClick={() => onSelectForLink(att.AttachmentID)}>Link to Well</button>
                    <button onClick={() => handleDeleteAttachment(att.AttachmentID)}>Delete</button>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <TreeView 
          wells={wells} 
          attachments={attachments} 
          baseUrl={baseUrl} 
          onDelete={handleDeleteAttachment} 
          onSelectForLink={onSelectForLink} 
          renderThumbnail={renderThumbnail} 
          getWellName={getWellName}
        />
      )}

      {selectedAttachmentId && (
        <div className="link-form">
          <h3>Link Attachment ID: {selectedAttachmentId} to Well</h3>
          <select value={linkWellId} onChange={(e) => setLinkWellId(e.target.value)} required>
            <option value="">Select Well</option>
            {wells.map(w => (
              <option key={w.WellID} value={w.WellID}>{w.WellName} ({w.WellID})</option>
            ))}
          </select>
          <button onClick={handleLink}>Confirm Link</button>
          <button onClick={() => setSelectedAttachmentId(null)}>Cancel</button>
        </div>
      )}
    </div>
  );
}